import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

/**
 * AUTH CONTEXT
 *
 * Security features:
 * 1. sessionStorage — credentials are wiped when the browser tab/window is closed.
 *    The user MUST log in again after closing and reopening the app.
 * 2. Inactivity auto-logout is handled in StudentLayout (and any other layout)
 *    via the useInactivityLogout hook.
 */

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Use sessionStorage → clears automatically when tab/browser is closed
    const checkAuthStatus = async () => {
      const savedUser = sessionStorage.getItem('exam_portal_user');
      const token = sessionStorage.getItem('exam_portal_token');

      if (savedUser && token) {
        try {
          setUser(JSON.parse(savedUser));
        } catch {
          sessionStorage.removeItem('exam_portal_user');
          sessionStorage.removeItem('exam_portal_token');
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post('/api/auth/login', { email, password });

      // Store in sessionStorage (not localStorage) — gone on tab close
      sessionStorage.setItem('exam_portal_user', JSON.stringify(data.user));
      sessionStorage.setItem('exam_portal_token', data.token);
      setUser(data.user);

      return data;
    } catch (error) {
      throw error;
    }
  };

  const register = async (name, email, password, role, enrollmentNo, subjects = []) => {
    try {
      const { data } = await api.post('/api/auth/register', {
        name, email, password, role, enrollmentNo, subjects,
      });

      sessionStorage.setItem('exam_portal_user', JSON.stringify(data.user));
      sessionStorage.setItem('exam_portal_token', data.token);
      setUser(data.user);

      return data;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    sessionStorage.removeItem('exam_portal_user');
    sessionStorage.removeItem('exam_portal_token');
    // Also clean any leftover localStorage keys from the old implementation
    localStorage.removeItem('exam_portal_user');
    localStorage.removeItem('exam_portal_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
